// References
// - https://dotblogs.com.tw/wasichris/2018/09/06/001935

import Vue from 'vue'
import * as types from './mutationTypes'
import { PERMISSION_API } from '@/utils/apis'
import { ADMIN1_DISABLED_PATHS, ADMIN2_REDIRECT_PATHS } from '@/utils/constants'

let enableLoadingMaskTime = Date.now()

const isAdmin1Disabled = href =>
  ADMIN1_DISABLED_PATHS.some(path =>
    path.endsWith('/') ? href.startsWith(path) : href === path
  )

const getters = {
  isLoading: state => state.loadingCounter > 0,
  hasPermission: state => permission => state.permissions.includes(permission)
}

const state = () => ({
  loadingCounter: 0,
  isEnableLoadingMask: false,
  permissions: [],
  menuItems: [],
  breadcrumbs: []
})

const mutations = {
  [types.APP_INCREASE_LOADING_COUNTER](state) {
    state.loadingCounter += 1
  },
  [types.APP_DECREASE_LOADING_COUNTER](state) {
    state.loadingCounter -= 1
  },
  [types.APP_SET_IS_ENABLE_LOADING_MASK](state, isEnable) {
    state.isEnableLoadingMask = isEnable
  },
  [types.APP_SET_MENU_ITEMS](state, permissionGroups) {
    // const home = {
    //   href: '/',
    //   title: 'Home',
    //   name: 'Home',
    //   icon: 'mdi mdi-home'
    // }

    const additions = []
    let permissions = []
    let items = []

    for (let permissionGroup of permissionGroups) {
      if (permissionGroup.main_systems) {
        const sub_systems = permissionGroup.main_systems
          .filter(s => s.status)
          .reduce((list, main) => {
            if (!main.sub_systems) {
              return list
            }

            const perms = main.sub_systems
              .filter(s => s.status === '1')
              .map(sub =>
                sub.name ? `/${main.name}/${sub.name}` : `/${main.name}`
              )

            for (const p of perms) {
              if (list.includes(p)) {
                continue
              }

              list.push(p)
            }

            return list
          }, [])

        for (const sub_system of sub_systems) {
          if (!permissions.includes(sub_system)) {
            permissions.push(sub_system)
          }
        }

        const main_systems = permissionGroup.main_systems.filter(
          s => s.status && s.is_menu
        )

        for (const main of main_systems) {
          let systems = main.sub_systems
            .filter(s => s.status === '1' && s.is_menu)
            .filter(s => s.version >= 2)

          if (systems.length === 0) {
            continue
          }

          let itemIdx = items.findIndex(t => t.name === main.name)
          const item =
            itemIdx !== -1
              ? items[itemIdx]
              : {
                  exactPath: true,
                  title: main.title,
                  name: main.name,
                  child: [],
                  icon: `mdi mdi-${main.icon || 'cat'}`
                }

          for (const sub of systems) {
            if (item.child.findIndex(s => s.name === sub.name) === -1) {
              const href = sub.name
                ? `/${main.name.toDash()}/${sub.name.toDash()}`
                : `/${main.name.toDash()}`

              item.child.push({
                disabled: sub.version < 2,
                href,
                exactPath: true,
                title: sub.title,
                name: sub.name,
                icon: sub.icon && `mdi mdi-${sub.icon}`,
                class: isAdmin1Disabled(href)
                  ? 'vsm--link_admin1-disabled'
                  : undefined,
                badge: ADMIN2_REDIRECT_PATHS.includes(href)
                  ? {
                      element: 'i',
                      class: 'mdi mdi-open-in-new vsm--badge_icon',
                      attributes: { title: '已移至後台 2.0' }
                    }
                  : isAdmin1Disabled(href)
                  ? {
                      element: 'i',
                      class:
                        'mdi mdi-cancel vsm--badge_icon vsm--badge_disabled',
                      attributes: { title: '已停用' }
                    }
                  : undefined
              })
            }
          }

          if (itemIdx !== -1) {
            items[itemIdx] = item
          } else {
            items.push(item)
          }
        }
      }
    }

    const menuItems = [...items, ...additions]

    const breadcrumbs = menuItems.reduce((list, item) => {
      const main = {
        path: item.href || `/${item.name.toDash()}`,
        title: item.title,
        name: item.name
      }

      let childList = []

      if (item.child) {
        childList = item.child.map(child => ({
          path: child.href,
          link: child.href,
          title: child.title,
          name: item.name
        }))
      }

      return [...list, main, ...childList]
    }, [])

    state.permissions = permissions
    state.menuItems = menuItems
    state.breadcrumbs = breadcrumbs
  }
}

const actions = {
  // 讀取中 api 數量加 1
  increaseLoadingCounter({ dispatch, commit, state }) {
    commit(types.APP_INCREASE_LOADING_COUNTER)
    // 目前仍有 api 在讀取中時，啟動遮罩
    if (state.loadingCounter > 0 && !state.isEnableLoadingMask) {
      dispatch('enableLoadingMask')
    }
  },
  // 讀取中 api 數量減 1
  decreaseLoadingCounter({ dispatch, commit, state }) {
    commit(types.APP_DECREASE_LOADING_COUNTER)
    // 目前沒有 api 在讀取中時，關閉遮罩
    if (state.loadingCounter <= 0 && state.isEnableLoadingMask) {
      dispatch('disableLoadingMask')
    }
  },
  // 啟動遮罩
  enableLoadingMask({ commit }) {
    enableLoadingMaskTime = Date.now()
    commit(types.APP_SET_IS_ENABLE_LOADING_MASK, true)
  },
  // 關閉遮罩
  disableLoadingMask({ commit, state }) {
    // 避免切換速度過快而造成畫面閃動，所以定義最小顯示時間
    const minMaskShowPeriod = 300 /* ms */
    const pastMilliseconds = Date.now() - enableLoadingMaskTime
    const isShorterThanMinMaskShowPeriod = minMaskShowPeriod > pastMilliseconds
    const remainMilliseconds = minMaskShowPeriod - pastMilliseconds

    if (process.client) {
      // 若低於最小顯示時間，將使用 setTimout 補足顯示時間後關閉
      setTimeout(
        () => {
          // 真正要關閉時，要確認目前是否還有 Request 執行中(避免延遲過程中又發出 request 被馬上關閉)
          if (state.loadingCounter <= 0 && state.isEnableLoadingMask) {
            commit(types.APP_SET_IS_ENABLE_LOADING_MASK, false)
          }
        },
        isShorterThanMinMaskShowPeriod ? remainMilliseconds : 0
      )
    } else {
      // 真正要關閉時，要確認目前是否還有 Request 執行中(避免延遲過程中又發出 request 被馬上關閉)
      if (state.loadingCounter <= 0 && state.isEnableLoadingMask) {
        commit(types.APP_SET_IS_ENABLE_LOADING_MASK, false)
      }
    }
  },
  // 設定左邊選單
  setMenuItems({ commit }, permissionGroups) {
    commit(types.APP_SET_MENU_ITEMS, permissionGroups)
  },
  fetchPermissions({ commit }) {
    const api = PERMISSION_API

    Vue.axios
      .get(api)
      .then(res => {
        const permissionGroups = res.data.data
        commit(types.APP_SET_MENU_ITEMS, permissionGroups)
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(api, error)
      })
  }
}

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions
}
